import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { TYPE_COLORS, TYPE_NAMES_RU, getDeviceStatus } from './equipmentTypes';
import type { RackEquipmentItem } from './rackTypes';

// ─────────────────────────────────────────────────────────────────────────────
// ZeroUEquipment — PDU/UPS in vertical side columns (0U mounting)
// ─────────────────────────────────────────────────────────────────────────────

interface ZeroUEquipmentProps {
  eq: RackEquipmentItem;
  isMountingMode?: boolean;
  onClick?: () => void;
  isDark?: boolean;
  pduSummary?: { free_outlets: number; total_outlets: number } | null;
  onOpenOutletMatrix?: () => void;
}

export function ZeroUEquipment({
  eq,
  isMountingMode,
  onClick,
  isDark = true,
  pduSummary,
  onOpenOutletMatrix,
}: ZeroUEquipmentProps) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: eq.id,
    data: { equipment: eq },
    disabled: !isMountingMode,
  });

  const style: React.CSSProperties = {
    zIndex:    isDragging ? 50 : 10,
    opacity:   isDragging ? 0.8 : 1,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    height:    '90px',
    width:     '100%',
  };

  const bgColor = TYPE_COLORS[eq.equipment_type] || TYPE_COLORS.unknown;
  const status  = getDeviceStatus(eq.status);
  const isPdu   = eq.equipment_type?.toLowerCase() === 'pdu';

  // Extract feed designation for instant visual recognition
  let feedLetter: string | null = null;
  try {
    const rawAttrs = typeof eq.attributes === 'string' ? JSON.parse(eq.attributes || '{}') : eq.attributes || {};
    const feedAttr = rawAttrs.feed_name || rawAttrs.feed;
    if (feedAttr) {
      feedLetter = String(feedAttr).replace(/feed/i, '').trim() || null;
    }
  } catch {
    // ignore parse error
  }
  if (!feedLetter && eq.mounting_position) {
    const m = eq.mounting_position.toLowerCase();
    if (m === 'left') feedLetter = 'A';
    else if (m === 'right') feedLetter = 'B';
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isMountingMode ? listeners : {})}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className={`relative rounded border border-[var(--border-color)] bg-[var(--bg-panel)] p-1 flex flex-col justify-between font-mono cursor-pointer transition-all ${
        isDragging ? 'shadow-[0_0_15px_rgba(58,95,138,0.6)] border-[var(--accent)] ring-2 ring-[var(--accent)]' : ''
      }`}
    >
      {/* Color overlay */}
      <div
        className="absolute inset-0 rounded pointer-events-none"
        style={{ backgroundColor: bgColor, opacity: isDark ? 0.20 : 0.30 }}
      />

      {/* Type header */}
      <div className="flex justify-between items-center text-[8px] font-bold z-10 text-[var(--text-secondary)]">
        <span>0U</span>
        <div className="flex items-center gap-1">
          {feedLetter && (
            <span
              className={`px-1 rounded-xs text-[7px] font-bold border uppercase tracking-wider ${
                feedLetter === 'A'
                  ? 'bg-cyan-950/70 text-cyan-300 border-cyan-700/60'
                  : 'bg-indigo-950/70 text-indigo-300 border-indigo-700/60'
              }`}
            >
              {feedLetter}
            </span>
          )}
          <span className="uppercase text-[7px] opacity-75">{TYPE_NAMES_RU[eq.equipment_type] || eq.equipment_type}</span>
        </div>
      </div>

      {/* Name */}
      <div className="text-[9px] font-semibold truncate z-10 my-auto text-[var(--text-primary)]" title={eq.name}>
        {eq.name}
      </div>

      {/* Status dot */}
      <div className="absolute top-1 right-1 flex gap-0.5">
        <span className={`w-1.5 h-1.5 rounded-full ${
          status === 'critical' ? 'bg-red-500 animate-ping' :
          status === 'warning'  ? 'bg-amber-500' : 'bg-emerald-400'
        }`} />
      </div>

      {/* PDU outlet button (full width, responsive) */}
      {pduSummary && (
        <div className="z-10 mt-auto pt-1 w-full">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenOutletMatrix?.();
            }}
            title="Открыть матрицу розеток PDU (24-портовая панель)"
            aria-label={`PDU: ${pduSummary.free_outlets} из ${pduSummary.total_outlets} свободно. Нажмите для открытия матрицы розеток`}
            className={`w-full flex items-center justify-between px-1 py-0.5 rounded-xs text-[8px] font-mono font-bold tabular-nums cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] border ${
              pduSummary.free_outlets === 0
                ? 'border-red-800/60 bg-red-950/80 text-red-300 hover:bg-red-900/90'
                : pduSummary.free_outlets <= 2
                ? 'border-amber-800/60 bg-amber-950/80 text-amber-300 hover:bg-amber-900/90'
                : 'border-emerald-800/60 bg-emerald-950/80 text-emerald-300 hover:bg-emerald-900/90'
            }`}
          >
            <span className="opacity-75 text-[7px] uppercase tracking-tight">РОЗ</span>
            <span>{pduSummary.free_outlets}/{pduSummary.total_outlets}</span>
          </button>
        </div>
      )}

      {/* Feed badge if PDU without summary */}
      {!pduSummary && isPdu && feedLetter && (
        <div className="z-10 mt-auto pt-1 w-full">
          {onOpenOutletMatrix ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenOutletMatrix();
              }}
              title="Открыть матрицу розеток PDU (24-портовая панель)"
              aria-label={`PDU ввод ${feedLetter}. Нажмите для открытия матрицы розеток`}
              className={`w-full block text-center text-[7px] font-mono font-bold px-1 py-0.5 rounded-xs border uppercase cursor-pointer transition-all hover:scale-[1.02] active:scale-[0.98] ${
                feedLetter === 'A'
                  ? 'bg-cyan-950/60 text-cyan-300 border-cyan-800/60 hover:bg-cyan-900/80'
                  : 'bg-indigo-950/60 text-indigo-300 border-indigo-800/60 hover:bg-indigo-900/80'
              }`}
            >
              ВВОД {feedLetter}
            </button>
          ) : (
            <span className={`block text-center text-[7px] font-mono font-bold px-1 py-0.5 rounded-xs border uppercase ${
              feedLetter === 'A'
                ? 'bg-cyan-950/60 text-cyan-300 border-cyan-800/60'
                : 'bg-indigo-950/60 text-indigo-300 border-indigo-800/60'
            }`}>
              ВВОД {feedLetter}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZeroUGhostPocket — Interactive dashed pocket for unequipped Zero-U channels
// ─────────────────────────────────────────────────────────────────────────────

export interface ZeroUGhostPocketProps {
  mount: 'Left' | 'Right';
  label?: string;
  isMountingMode?: boolean;
  onClick?: () => void;
}

export function ZeroUGhostPocket({
  mount,
  label,
  isMountingMode,
  onClick,
}: ZeroUGhostPocketProps) {
  const defaultLabel = mount === 'Left' ? '+ PDU A (0U)' : '+ PDU B (0U)';
  const displayLabel = label || defaultLabel;
  const feedName = mount === 'Left' ? 'Feed A' : 'Feed B';
  const isLeft = mount === 'Left';

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Empty Zero-U ${mount} PDU pocket (${feedName}), activate to add PDU`}
      className={`w-full min-h-[96px] h-full rounded border border-dashed flex flex-col items-center justify-center p-2 text-center group cursor-pointer focus:outline-none focus:ring-1 focus:ring-[var(--accent)] select-none transition-all ${
        isMountingMode
          ? isLeft
            ? 'border-cyan-500/80 bg-cyan-950/20'
            : 'border-indigo-500/80 bg-indigo-950/20'
          : isLeft
          ? 'border-[var(--border-color)] bg-[var(--bg-deep)]/30 hover:bg-cyan-950/15 hover:border-cyan-500/60'
          : 'border-[var(--border-color)] bg-[var(--bg-deep)]/30 hover:bg-indigo-950/15 hover:border-indigo-500/60'
      }`}
    >
      <span className={`text-base font-bold leading-none mb-1 text-[var(--text-muted)] transition-colors ${
        isLeft ? 'group-hover:text-cyan-300' : 'group-hover:text-indigo-300'
      }`}>
        +
      </span>
      <span className="font-mono text-[8px] font-semibold tracking-tight uppercase leading-tight text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
        {displayLabel}
      </span>
      <span className={`font-mono text-[7px] text-[var(--text-muted)] opacity-75 mt-1 ${
        isLeft ? 'group-hover:text-cyan-400' : 'group-hover:text-indigo-400'
      }`}>
        {feedName}
      </span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZeroUDroppableColumn — droppable container for 0U equipment side column
// ─────────────────────────────────────────────────────────────────────────────

interface ZeroUDroppableColumnProps {
  mount: string;
  children: React.ReactNode;
  onClick?: () => void;
}

export function ZeroUDroppableColumn({ mount, children, onClick }: ZeroUDroppableColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `zero-u-${mount}`,
    data: { isZeroU: true, mount },
  });

  return (
    <div
      ref={setNodeRef}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClick) {
          onClick();
        }
      }}
      className={`flex-1 p-1 space-y-1 overflow-y-auto flex flex-col rounded transition-colors duration-150 ${
        isOver
          ? 'bg-[var(--bg-active)] ring-2 ring-[var(--accent)]'
          : ''
      }`}
    >
      {children}
    </div>
  );
}
